import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { useT } from '@/contexts/I18nContext';
import { uploadZonePlanImage, deleteZonePlanImage } from '../api/zones';
import { useCurrentZone } from '../hooks/useCurrentZone';
import { BranchRequiredState } from '@/components/common/BranchRequiredState';
import { extractApiError } from '../utils/apiError';
import { API_HOST } from '@/api/base';
import { toAbsoluteUrl } from '@/api/url';
import BranchPlanAutoLayout from './BranchPlanAutoLayout';

const BranchPlanSettings: React.FC = () => {
  const { t } = useT();
  const { zoneId, currentZone, mutate: mutateZones } = useCurrentZone();
  const [planError, setPlanError] = useState<string | null>(null);
  const [planUploading, setPlanUploading] = useState(false);
  const planInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setPlanError(null); }, [zoneId]);

  async function handleUploadPlan(e: React.ChangeEvent<HTMLInputElement>) {
    if (!zoneId) return;
    const file = e.target.files?.[0];
    if (!file) return;
    setPlanError(null);
    setPlanUploading(true);
    try {
      await uploadZonePlanImage(zoneId, file);
      await mutateZones();
    } catch (err) {
      setPlanError(extractApiError(err, t('error.savingName')));
    } finally {
      setPlanUploading(false);
      if (planInputRef.current) planInputRef.current.value = '';
    }
  }

  async function handleDeletePlan() {
    if (!zoneId) return;
    setPlanError(null);
    setPlanUploading(true);
    try {
      await deleteZonePlanImage(zoneId);
      await mutateZones();
    } catch (err) {
      setPlanError(extractApiError(err, t('error.savingName')));
    } finally {
      setPlanUploading(false);
    }
  }

  if (!zoneId) {
    return <BranchRequiredState />;
  }

  return (
    <div className="space-y-4">
    <Card variant="section">
      <CardContent className="space-y-4">
        <section>
          <div className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
            {t('general.planImage')}
          </div>
          {currentZone?.plan_image ? (
            <div className="space-y-3">
              <div className="overflow-hidden rounded-md border bg-muted/30">
                <img
                  src={toAbsoluteUrl(currentZone.plan_image, API_HOST)}
                  alt={t('general.planImage')}
                  className="block max-h-[640px] w-full object-contain"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={planUploading}
                  onClick={() => planInputRef.current?.click()}
                  className="rounded bg-muted px-3 py-1 text-sm hover:bg-muted/80 disabled:opacity-50"
                >
                  {t('general.planImage.replace')}
                </button>
                <button
                  type="button"
                  disabled={planUploading}
                  onClick={handleDeletePlan}
                  className="rounded bg-destructive/10 px-3 py-1 text-sm text-destructive hover:bg-destructive/20 disabled:opacity-50"
                >
                  {t('general.planImage.delete')}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              disabled={planUploading}
              onClick={() => planInputRef.current?.click()}
              className="flex h-32 w-full items-center justify-center rounded border border-dashed text-sm text-muted-foreground hover:border-foreground/40 hover:text-foreground disabled:opacity-50"
            >
              {planUploading ? '…' : t('general.planImage.upload')}
            </button>
          )}
          <input
            ref={planInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleUploadPlan}
          />
          {planError && (
            <div className="mt-2 max-w-md rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {planError}
            </div>
          )}
        </section>
      </CardContent>
    </Card>
    <BranchPlanAutoLayout />
    </div>
  );
};

export default BranchPlanSettings;
